import { useState, useEffect } from "react";
import { MinusIcon, PlusIcon } from "@heroicons/react/24/solid";
import Button from "components/Button";
import Modal from "components/Modal";
import { formatCurrency } from "helpers/currency";
import VariantSwatchSelector from "./VariantSwatchSelector";
import ProductDescription from "./ProductDescription";
import type { KeepsakeProduct } from "./types";

type KeepsakeDetailModalProps = {
  keepsake: KeepsakeProduct | null;
  isOpen: boolean;
  onClose: () => void;
  onSelect: (productId: string, variantId: string, quantity: number) => void;
  currentVariantId?: string | null;
  currentQuantity?: number;
  readOnly?: boolean;
};

export default function KeepsakeDetailModal({
  keepsake,
  isOpen,
  onClose,
  onSelect,
  currentVariantId,
  currentQuantity,
  readOnly = false,
}: KeepsakeDetailModalProps) {
  const [quantity, setQuantity] = useState(1);
  const [activeImageSrc, setActiveImageSrc] = useState<string | null>(null);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (keepsake) {
      setQuantity(currentQuantity || 1);
      const initialVariantId =
        (currentVariantId || keepsake.variants?.[0]?.id) ?? null;
      const initialVariant = keepsake.variants?.find(
        (v) => v.id === initialVariantId,
      );
      const images = keepsake.images?.length
        ? keepsake.images
        : keepsake.image
          ? [keepsake.image]
          : [];

      setActiveImageSrc(initialVariant?.image ?? images[0] ?? null);
      setSelectedVariantId(initialVariantId);
    }
  }, [keepsake]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!keepsake) return null;

  const allImages = keepsake.images?.length
    ? keepsake.images
    : keepsake.image
      ? [keepsake.image]
      : [];

  const hasVariants = !!keepsake.variants?.length;
  const selectedVariant = hasVariants
    ? keepsake.variants!.find((v) => v.id === selectedVariantId)
    : null;

  // When a variant is selected, show its image as the main image.
  // The thumbnail strip still shows the full gallery for browsing.
  const mainImage = activeImageSrc ?? selectedVariant?.image;

  return (
    <Modal isOpen={isOpen} onClose={onClose} fullScreen>
      <div className="flex h-full flex-col md:flex-row md:gap-10">
        {/* Left: Image gallery */}
        {/* Mobile pt-8 clears Modal's absolutely-positioned close button */}
        <div className="flex shrink-0 flex-col pt-8 md:w-1/2 md:pt-0">
          {/* Main image */}
          <div className="flex flex-1 items-center justify-center">
            {mainImage ? (
              <img
                src={mainImage}
                alt={keepsake.name}
                className="max-h-[40vh] w-full object-contain md:max-h-[60vh]"
              />
            ) : (
              <div
                className="h-64 w-full rounded-xl md:h-96"
                style={{ backgroundColor: keepsake.color }}
              />
            )}
          </div>

          {/* Thumbnail strip */}
          {allImages.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto pb-2 md:mt-4">
              {allImages.map((src, i) => (
                <button
                  key={i}
                  onClick={() => {
                    const variant = keepsake.variants?.find(
                      (v) => v.image === src,
                    );
                    setActiveImageSrc(src);
                    if (variant) {
                      setSelectedVariantId(variant.id);
                    }
                  }}
                  className={`h-16 w-16 shrink-0 overflow-hidden rounded-lg border-2 ${
                    src === activeImageSrc
                      ? "border-evergreen"
                      : "border-transparent opacity-70 hover:opacity-100"
                  }`}
                >
                  <img
                    src={src}
                    alt={`${keepsake.name} view ${i + 1}`}
                    className="h-full w-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right: Details */}
        <div className="flex flex-col md:w-1/2 md:overflow-y-auto md:py-2">
          {/* Name + Price */}
          <div>
            <h2 className="font-serif text-3xl italic text-evergreen md:text-4xl">
              {keepsake.name}
            </h2>
            <p className="mt-1 text-2xl font-bold text-evergreen">
              {formatCurrency(selectedVariant?.price ?? keepsake.price)}
            </p>
          </div>

          {/* Description */}
          <ProductDescription
            description={keepsake.description}
            descriptionHtml={keepsake.descriptionHtml}
          />

          {/* Specs */}
          {(keepsake.material || keepsake.dimensions) && (
            <dl className="mt-4 grid grid-cols-2 gap-x-8 gap-y-3 border-b border-t border-oat py-4">
              {keepsake.material && (
                <div>
                  <dt className="font-semibold text-evergreen">Material</dt>
                  <dd className="mt-1 text-soil">{keepsake.material}</dd>
                </div>
              )}
              {keepsake.dimensions && (
                <div>
                  <dt className="font-semibold text-evergreen">Dimensions</dt>
                  <dd className="mt-1 text-soil">{keepsake.dimensions}</dd>
                </div>
              )}
            </dl>
          )}

          {/* Color swatches + Quantity */}
          <div className="mt-6 flex items-end justify-between gap-4">
            {/* Color swatches */}
            {hasVariants && (
              <VariantSwatchSelector
                variants={keepsake.variants!}
                selectedVariantId={selectedVariantId}
                onSelect={(variantId) => {
                  setSelectedVariantId(variantId);
                  const variant = keepsake.variants!.find(
                    (v) => v.id === variantId,
                  );
                  if (variant) {
                    setActiveImageSrc(variant.image);
                  }
                }}
                readOnly={readOnly}
              />
            )}

            {/* Quantity */}
            <div className="shrink-0">
              <p className="font-semibold text-evergreen">Quantity</p>
              {readOnly ? (
                <p className="mt-2 text-center text-xl font-bold text-evergreen">
                  {quantity}
                </p>
              ) : (
                <div className="mt-2 flex items-center gap-4">
                  <button
                    onClick={() => setQuantity((q) => Math.max(0, q - 1))}
                    disabled={quantity <= 0}
                    className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-oat text-evergreen transition hover:bg-oat disabled:opacity-40 disabled:hover:bg-transparent"
                    aria-label="Decrease quantity"
                  >
                    <MinusIcon className="h-5 w-5" />
                  </button>
                  <span className="min-w-6 text-center text-xl font-bold text-evergreen">
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity((q) => q + 1)}
                    className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-oat text-evergreen transition hover:bg-oat"
                    aria-label="Increase quantity"
                  >
                    <PlusIcon className="h-5 w-5" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* CTA pinned to bottom */}
          {!readOnly && (
            <div className="mt-auto pt-6">
              <Button
                onClick={() => {
                  onSelect(keepsake.id, selectedVariantId || "", quantity);
                  onClose();
                }}
                variant="primary"
                fullWidth
              >
                {currentQuantity ? "Update selection" : "Add keepsake"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
